import { sum } from 'd3';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { isValidNumber } from '../../../../../../../utils/math/number';
import {
  getLineSegmentFaces,
  LineSegmentNode,
} from '../../../../../../../viewer/elements/design/design-node/utils/line-segment-node';
import { DesignTool } from '../../../../../../../viewer/tools/design-tool';
import { lineSegmentFunctions } from '../../../../../../../viewer/tools/utils/context-menu/design-tool/commands/features/generate-line-segments';
import { listLinesAndPolygonsBySelection } from '../../../../../../../viewer/tools/utils/context-menu/design-tool/utils/multi-select';
import { useUnitSymbols } from '../../../../../../hooks/use-unit-symbols';
import { Button } from '../../../../../button/button';
import { InfoBox } from '../../../../../info-box/info-box';
import { LabelledContainer } from '../../../../../labelled-container/labelled-container';
import { Stack } from '../../../../../stack/stack';
import { TextBox } from '../../../../../text-box/text-box';
import { useCurrentSelection } from '../hooks/use-current-selection';

type Props = {
  tool: DesignTool;
};

enum Status {
  Init,
  Idle,
  Loading,
  Error,
}

const pointCountWarningTreshold = 15000;
const minLineSegmentLength = 0.02;

const GenerateLineSegments = (props: Props) => {
  const { t } = useTranslation();
  const unitSymbols = useUnitSymbols();
  const [status, setStatus] = React.useState(Status.Init);
  const [length, setLength] = React.useState(0);
  const [stepSize, setStepSize] = React.useState<string>('1');
  const [selectedGeometry, setSelectedGeometry] = React.useState<LineSegmentNode[]>([]);
  const { currentSelection } = useCurrentSelection(props.tool);

  const isLoading = [Status.Init, Status.Loading].includes(status);
  const isInputValid = isValidNumber(Number(stepSize)) && Number(stepSize) >= minLineSegmentLength;
  const pointCountEstimate = isInputValid ? Math.floor(length / Number(stepSize)) : 0;

  const refresh = React.useCallback(() => {
    const selectedLines = currentSelection.lineSegments.filter(
      (x) => getLineSegmentFaces(props.tool.asset.designNode, x).length === 0,
    );

    const result = listLinesAndPolygonsBySelection(props.tool.asset.designNode, selectedLines);
    const lineSegmentNodes = result.flatMap((x) => x.lines);

    const totalLength = sum(lineSegmentNodes.map((x) => x.length));

    const calculateStepSize = (desiredNumberOfPoints: number) => {
      const size = totalLength / desiredNumberOfPoints;
      return size < minLineSegmentLength ? minLineSegmentLength : size;
    };

    const initialStepSize = totalLength > 1000 ? calculateStepSize(1000) : 1;

    setSelectedGeometry(lineSegmentNodes);
    setLength(totalLength);
    setStepSize(initialStepSize.toFixed(2));
    setStatus(Status.Idle);
  }, [props.tool, currentSelection]);

  React.useEffect(refresh, [refresh]);

  return (
    <Stack spacing={1}>
      {pointCountEstimate >= pointCountWarningTreshold ? (
        <InfoBox
          color="yellow"
          leftIcon={{ icon: 'exclamation' }}
          title={t('section.functions.lineSegment.infoSelectionTresholdExceeded.title', {
            ns: 'skyviewDesign',
          })}
        >
          <Trans
            components={{ br: <br />, bold: <strong /> }}
            i18nKey="section.functions.lineSegment.infoSelectionTresholdExceeded.text"
            ns="skyviewDesign"
            values={{
              count: pointCountEstimate,
              threshold: pointCountWarningTreshold,
            }}
          />
        </InfoBox>
      ) : (
        <InfoBox color="yellow">
          <Trans
            components={{ br: <br />, bold: <strong /> }}
            i18nKey="section.functions.lineSegment.infoSelectionTresholdNotExceeded.text"
            ns="skyviewDesign"
            values={{
              count: pointCountEstimate,
            }}
          />
        </InfoBox>
      )}

      <LabelledContainer
        text={t('section.functions.lineSegment.settings.distanceText', {
          ns: 'skyviewDesign',
          unit: unitSymbols.length,
        })}
      >
        <TextBox
          errorMessage={
            isInputValid
              ? undefined
              : t('section.functions.lineSegment.settings.distanceErrorMessage', {
                  minLineSegmentLength,
                  ns: 'skyviewDesign',
                })
          }
          min={minLineSegmentLength}
          type={'number'}
          value={stepSize}
          onChange={(e) => setStepSize(e.target.value)}
        />
      </LabelledContainer>
      <Button
        color={'primary'}
        disabled={!isInputValid || isLoading}
        loading={isLoading}
        variant="contained"
        onClick={() => {
          lineSegmentFunctions.applyGenerateLineSegmentsResult(
            props.tool,
            selectedGeometry,
            Number(stepSize),
          );
        }}
      >
        {t('confirm', { ns: 'common' })}
      </Button>
    </Stack>
  );
};

export { GenerateLineSegments };
